import { forwardRef, useState } from 'react';

const services = [
  {
    number: '01',
    icon: 'ri-camera-lens-line',
    title: 'Media Mastery',
    headline: 'Command Every Lens. Own Every Headline.',
    description:
      'In the arena of global power, perception is policy. We train heads of state, ministers, and institutional leaders to dominate every media encounter — from live press conferences and hostile interviews to international broadcast appearances. You will never again be caught off-guard. Every frame, every headline, every narrative will be yours to shape.',
    detail: 'Press Conference Preparation · Broadcast Media Training · Crisis Communications · Narrative Control',
    image: 'https://public.readdy.ai/ai/img_res/e7a84776612693e8e6ed5e143f363f31.jpg',
  },
  {
    number: '02',
    icon: 'ri-user-star-line',
    title: 'Executive Presence',
    headline: 'Walk Into Any Room and Own It Before You Speak.',
    description:
      'True authority is felt before a word is uttered. Our executive presence coaching transforms how you carry yourself, how you enter a room, how you hold silence, and how you command attention at the highest levels of global diplomacy and business. We work with the body, the voice, the gaze — until your presence alone signals power.',
    detail: 'Body Language Mastery · Voice & Gravitas Coaching · Protocol & Etiquette · Diplomatic Presence',
    image: 'https://readdy.ai/api/search-image?query=Majestic%20grand%20marble%20corridor%20of%20a%20prestigious%20international%20institution%2C%20soaring%20arched%20ceilings%20with%20ornate%20plasterwork%2C%20polished%20white%20marble%20floors%20reflecting%20warm%20amber%20light%20from%20brass%20wall%20sconces%2C%20long%20dramatic%20perspective%20leading%20to%20tall%20arched%20windows%2C%20deep%20shadows%20and%20golden%20highlights%2C%20sense%20of%20supreme%20authority%20and%20institutional%20gravitas%2C%20ultra-wide%20cinematic%208K%20photography%2C%20no%20people%2C%20photorealistic%20architectural%20interior%2C%20luxury%20diplomatic%20building&width=1200&height=800&seq=service-exec-presence-hd-v6&orientation=landscape',
  },
  {
    number: '03',
    icon: 'ri-quill-pen-line',
    title: 'Message Architecture',
    headline: 'Craft Words That Move Nations and Shape Policy.',
    description:
      'The most powerful leaders in history were defined by what they said and how they said it. We architect the language of governance — building speeches, policy statements, and diplomatic communiqués that resonate across cultures, move international audiences, and endure as institutional legacy. Your words will carry the weight of sovereignty.',
    detail: 'Speechwriting · Policy Messaging · Diplomatic Communiqués · Sovereign Narrative Strategy',
    image: 'https://readdy.ai/api/search-image?query=Exquisite%20close-up%20of%20an%20open%20leather-bound%20journal%20with%20cream%20pages%20and%20a%20single%20gold-nibbed%20fountain%20pen%20resting%20diagonally%20across%20it%2C%20dark%20polished%20mahogany%20desk%20surface%2C%20warm%20amber%20light%20from%20a%20brass%20desk%20lamp%20casting%20a%20soft%20glow%2C%20blurred%20background%20of%20floor-to-ceiling%20bookshelves%20filled%20with%20leather-bound%20volumes%2C%20shallow%20depth%20of%20field%2C%20ultra-sharp%20macro%20photography%208K%2C%20no%20text%20visible%2C%20quiet%20luxury%20aesthetic%2C%20intellectual%20gravitas%2C%20warm%20amber%20and%20deep%20brown%20tones%2C%20photorealistic&width=1200&height=800&seq=service-message-arch-hd-v6&orientation=landscape',
  },
  {
    number: '04',
    icon: 'ri-shield-star-line',
    title: 'High-Stakes Briefing',
    headline: 'Prepare for Moments Where There Is No Second Chance.',
    description:
      'G7 summits. UN Security Council addresses. IMF negotiations. AU treaty sessions. These are the rooms where nations are made or diminished. We prepare you for every scenario — the hostile question, the unexpected pivot, the moment of silence that demands a response. When the stakes are absolute, Anteroom ensures you are ready.',
    detail: 'Summit Preparation · Negotiation Briefing · Scenario Simulation · Crisis Response Drills',
    image: 'https://readdy.ai/api/search-image?query=Vast%20empty%20international%20summit%20conference%20chamber%20with%20sweeping%20curved%20rows%20of%20dark%20wood%20delegate%20tables%2C%20gold%20microphones%20and%20national%20nameplates%2C%20dramatic%20overhead%20lighting%20casting%20warm%20pools%20of%20amber%20light%20on%20the%20chamber%20floor%2C%20rows%20of%20national%20flags%20softly%20blurred%20in%20background%2C%20immense%20institutional%20weight%20and%20global%20power%2C%20ultra-wide%20angle%208K%20architectural%20photography%2C%20no%20people%2C%20deep%20perspective%2C%20muted%20tones%20of%20dark%20wood%20and%20gold%2C%20United%20Nations%20Security%20Council%20style%20chamber%2C%20photorealistic%2C%20ultra%20sharp&width=1200&height=800&seq=service-highstakes-hd-v6&orientation=landscape',
  },
];

const Services = forwardRef<HTMLElement>((props, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const active = services[activeIndex];

  return (
    <section ref={ref} className="bg-[#0D0C0A] py-[200px] md:py-[200px] py-[100px] opacity-0 transition-all duration-[800ms]" id="services">
      <div className="max-w-[1600px] mx-auto px-6 md:px-[80px]">

        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-[100px] md:mb-[100px] mb-[60px] gap-[32px] md:gap-0">
          <div className="text-center md:text-left">
            <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.45em] font-sans mb-[28px] md:mb-[28px] mb-[20px]">
              Our Disciplines
            </p>
            <h2
              className="font-serif text-[#F8F6F3] leading-[0.95] tracking-[-0.01em]"
              style={{ fontSize: 'clamp(36px, 5vw, 72px)', fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Four Pillars of<br />Sovereign Readiness
            </h2>
          </div>
          <p className="text-[#F8F6F3]/45 text-[14px] md:text-[15px] font-sans leading-[1.7] max-w-[420px] text-center md:text-right mx-auto md:mx-0">
            Every engagement is a performance. Every room is a stage.<br />
            We ensure you are never unprepared.
          </p>
        </div>

        {/* Tab selectors */}
        <div className="grid grid-cols-2 md:grid-cols-4 border border-[#3A3D42] mb-[80px] md:mb-[80px] mb-[60px]">
          {services.map((s, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className="flex flex-col items-center md:items-start px-[12px] md:px-[40px] py-[24px] md:py-[36px] border-r border-b md:border-b-0 border-[#3A3D42] last:border-r-0 md:[&:nth-child(2)]:border-r md:[&:nth-child(3)]:border-b-0 md:[&:nth-child(4)]:border-b-0 [&:nth-child(3)]:border-b-0 [&:nth-child(4)]:border-b-0 transition-all duration-400 cursor-pointer"
              style={{
                backgroundColor: activeIndex === i ? '#1A1917' : 'transparent',
                borderBottom: activeIndex === i ? '2px solid #C9A96E' : '2px solid transparent',
              }}
            >
              <span
                className="font-sans mb-3 text-center md:text-left"
                style={{
                  fontSize: '9px',
                  letterSpacing: '0.3em',
                  color: activeIndex === i ? '#C9A96E' : 'rgba(248,246,243,0.3)',
                }}
              >
                {s.number}
              </span>
              <span
                className="font-serif text-center md:text-left text-[14px] md:text-[20px] leading-tight"
                style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  color: activeIndex === i ? '#F8F6F3' : 'rgba(248,246,243,0.55)',
                  letterSpacing: '0.01em',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {s.title}
              </span>
            </button>
          ))}
        </div>

        {/* Active service detail */}
        <div className="flex flex-col md:grid md:grid-cols-2 gap-[60px] md:gap-[80px] items-center min-h-auto md:min-h-[520px]">
          {/* Image side - appears first on mobile */}
          <div className="relative overflow-hidden w-full h-[320px] md:h-[520px] order-1 md:order-2">
            <img
              key={activeIndex}
              src={active.image}
              alt={active.title}
              className="w-full h-full object-cover object-top"
              style={{
                filter: 'saturate(0.75) contrast(1.1)',
                animation: 'fadeInScale 0.8s ease forwards',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div
              className="absolute bottom-[40px] left-[40px] text-[#C9A96E] font-serif"
              style={{ fontSize: 'clamp(60px, 10vw, 80px)', lineHeight: 1, opacity: 0.12, fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              {active.number}
            </div>
          </div>

          {/* Text side - appears second on mobile */}
          <div className="order-2 md:order-1">
            <h3
              className="font-serif text-[#F8F6F3] mb-[40px] md:mb-[40px] mb-[28px] leading-[1.1]"
              style={{
                fontSize: 'clamp(22px, 3vw, 42px)',
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
              }}
            >
              {active.headline}
            </h3>
            <p className="text-[#F8F6F3]/65 text-[15px] md:text-[17px] font-sans leading-[1.85] tracking-[0.015em] mb-[56px] md:mb-[56px] mb-[40px]">
              {active.description}
            </p>
            <div className="border-t border-[#3A3D42] pt-[40px] md:pt-[40px] pt-[32px]">
              <p className="text-[#C9A96E] text-[10px] uppercase tracking-[0.3em] font-sans mb-[20px]">
                What We Cover
              </p>
              <p className="text-[#F8F6F3]/50 text-[12px] md:text-[13px] font-sans leading-[1.9] tracking-[0.04em]">
                {active.detail}
              </p>
            </div>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-4 mt-[56px] md:mt-[56px] mt-[40px] text-[#C9A96E] font-sans uppercase hover:opacity-70 transition-opacity duration-400 cursor-pointer whitespace-nowrap"
              style={{ fontSize: '10px', letterSpacing: '0.28em' }}
            >
              Enquire About This Discipline
              <span className="w-[20px] h-[1px] bg-[#C9A96E] inline-block" />
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(1.04); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </section>
  );
});

Services.displayName = 'Services';

export default Services;
